import { useCallback, useEffect, useState } from 'react';
import { SearchableSelect } from '@/backoffice/components/ui';
import {
  fetchCommunes,
  fetchCountries,
  fetchDistricts,
  fetchPrefectures,
  fetchRegions,
  fetchSecteurs,
  resolveSecteur,
  type GeoItem,
} from '@/domains/api/geography.api';
import { Loader2, MapPin } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface LocalizationValue {
  countryId: number | null;
  regionId: number | null;
  prefectureId: number | null;
  communeId: number | null;
  districtId: number | null;
  secteurId: number | null;
}

interface LocalizationInputGroupProps {
  value: LocalizationValue;
  onChange: (value: LocalizationValue) => void;
  disabled?: boolean;
  className?: string;
  showCoordinates?: boolean;
}

const EMPTY: LocalizationValue = {
  countryId: null,
  regionId: null,
  prefectureId: null,
  communeId: null,
  districtId: null,
  secteurId: null,
};

export function LocalizationInputGroup({
  value,
  onChange,
  disabled = false,
  className,
  showCoordinates = false,
}: LocalizationInputGroupProps) {
  const [loadingInit, setLoadingInit] = useState(true);
  const [countries, setCountries] = useState<GeoItem[]>([]);
  const [regions, setRegions] = useState<GeoItem[]>([]);
  const [prefectures, setPrefectures] = useState<GeoItem[]>([]);
  const [communes, setCommunes] = useState<GeoItem[]>([]);
  const [districts, setDistricts] = useState<GeoItem[]>([]);
  const [secteurs, setSecteurs] = useState<GeoItem[]>([]);
  const [coords, setCoords] = useState<{ lat?: number | null; lng?: number | null }>({});

  const loadRegions = useCallback(async (countryId: number) => {
    setRegions(await fetchRegions(countryId));
  }, []);

  const loadPrefectures = useCallback(async (regionId: number) => {
    setPrefectures(await fetchPrefectures(regionId));
  }, []);

  const loadCommunes = useCallback(async (prefectureId: number) => {
    setCommunes(await fetchCommunes(prefectureId));
  }, []);

  const loadDistricts = useCallback(async (communeId: number) => {
    setDistricts(await fetchDistricts(communeId));
  }, []);

  const loadSecteurs = useCallback(async (districtId: number) => {
    setSecteurs(await fetchSecteurs(districtId));
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoadingInit(true);
      try {
        const { countries: list, defaultCountry } = await fetchCountries();
        if (cancelled) return;
        setCountries(list);

        if (value.secteurId) {
          const hierarchy = await resolveSecteur(value.secteurId);
          if (cancelled) return;
          const country = hierarchy.country;
          const region = hierarchy.region;
          const prefecture = hierarchy.prefecture;
          const commune = hierarchy.commune;
          const district = hierarchy.district;

          if (country) {
            const r = await fetchRegions(country.id);
            if (cancelled) return;
            setRegions(r);
          }
          if (region) {
            const p = await fetchPrefectures(region.id);
            if (cancelled) return;
            setPrefectures(p);
          }
          if (prefecture) {
            const c = await fetchCommunes(prefecture.id);
            if (cancelled) return;
            setCommunes(c);
          }
          if (commune) {
            const d = await fetchDistricts(commune.id);
            if (cancelled) return;
            setDistricts(d);
          }
          if (district) {
            const s = await fetchSecteurs(district.id);
            if (cancelled) return;
            setSecteurs(s);
          }

          onChange({
            countryId: country?.id ?? null,
            regionId: region?.id ?? null,
            prefectureId: prefecture?.id ?? null,
            communeId: commune?.id ?? null,
            districtId: district?.id ?? null,
            secteurId: hierarchy.secteur.id,
          });
          setCoords({ lat: hierarchy.secteur.latitude, lng: hierarchy.secteur.longitude });
        } else if (defaultCountry) {
          const r = await fetchRegions(defaultCountry.id);
          if (cancelled) return;
          setRegions(r);
          onChange({ ...EMPTY, countryId: defaultCountry.id });
        }
      } finally {
        if (!cancelled) setLoadingInit(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCountry = async (countryId: number | null) => {
    onChange({ ...EMPTY, countryId });
    setRegions([]);
    setPrefectures([]);
    setCommunes([]);
    setDistricts([]);
    setSecteurs([]);
    setCoords({});
    if (countryId) await loadRegions(countryId);
  };

  const handleRegion = async (regionId: number | null) => {
    onChange({ ...value, regionId, prefectureId: null, communeId: null, districtId: null, secteurId: null });
    setPrefectures([]);
    setCommunes([]);
    setDistricts([]);
    setSecteurs([]);
    setCoords({});
    if (regionId) await loadPrefectures(regionId);
  };

  const handlePrefecture = async (prefectureId: number | null) => {
    onChange({ ...value, prefectureId, communeId: null, districtId: null, secteurId: null });
    setCommunes([]);
    setDistricts([]);
    setSecteurs([]);
    setCoords({});
    if (prefectureId) await loadCommunes(prefectureId);
  };

  const handleCommune = async (communeId: number | null) => {
    onChange({ ...value, communeId, districtId: null, secteurId: null });
    setDistricts([]);
    setSecteurs([]);
    setCoords({});
    if (communeId) await loadDistricts(communeId);
  };

  const handleDistrict = async (districtId: number | null) => {
    onChange({ ...value, districtId, secteurId: null });
    setSecteurs([]);
    setCoords({});
    if (districtId) await loadSecteurs(districtId);
  };

  const handleSecteur = (secteurId: number | null) => {
    onChange({ ...value, secteurId });
    const secteur = secteurs.find((s) => s.id === secteurId);
    setCoords({ lat: secteur?.latitude, lng: secteur?.longitude });
  };

  const toOptions = (items: GeoItem[]) => items.map((i) => ({ value: String(i.id), label: i.name }));

  const numVal = (v: number | null) => (v != null ? String(v) : undefined);

  if (loadingInit) {
    return (
      <div className={cn('flex items-center justify-center py-8 text-gray-500', className)}>
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Chargement de la localisation…
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
        <MapPin className="w-4 h-4 text-indigo-600" />
        Localisation
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <SearchableSelect
          label="Pays"
          value={numVal(value.countryId)}
          onChange={(v) => handleCountry(v ? Number(v) : null)}
          options={toOptions(countries)}
          placeholder="Sélectionner un pays"
          disabled={disabled}
        />
        <SearchableSelect
          label="Région"
          value={numVal(value.regionId)}
          onChange={(v) => handleRegion(v ? Number(v) : null)}
          options={toOptions(regions)}
          placeholder={value.countryId ? 'Sélectionner une région' : "Choisir d'abord un pays"}
          disabled={disabled || !value.countryId}
        />
        <SearchableSelect
          label="Préfecture"
          value={numVal(value.prefectureId)}
          onChange={(v) => handlePrefecture(v ? Number(v) : null)}
          options={toOptions(prefectures)}
          placeholder={value.regionId ? 'Sélectionner une préfecture' : "Choisir d'abord une région"}
          disabled={disabled || !value.regionId}
        />
        <SearchableSelect
          label="Commune"
          value={numVal(value.communeId)}
          onChange={(v) => handleCommune(v ? Number(v) : null)}
          options={toOptions(communes)}
          placeholder={value.prefectureId ? 'Sélectionner une commune' : "Choisir d'abord une préfecture"}
          disabled={disabled || !value.prefectureId}
        />
        <SearchableSelect
          label="Quartier (district)"
          value={numVal(value.districtId)}
          onChange={(v) => handleDistrict(v ? Number(v) : null)}
          options={toOptions(districts)}
          placeholder={value.communeId ? 'Sélectionner un quartier' : "Choisir d'abord une commune"}
          disabled={disabled || !value.communeId}
        />
        <SearchableSelect
          label="Secteur (carré)"
          value={numVal(value.secteurId)}
          onChange={(v) => handleSecteur(v ? Number(v) : null)}
          options={toOptions(secteurs)}
          placeholder={value.districtId ? 'Sélectionner un secteur' : "Choisir d'abord un quartier"}
          disabled={disabled || !value.districtId}
        />
      </div>

      {showCoordinates && value.secteurId && (coords.lat || coords.lng) && (
        <p className="text-xs text-gray-500">
          Coordonnées : {coords.lat ?? '—'}, {coords.lng ?? '—'}
        </p>
      )}
    </div>
  );
}
